import { useState, useEffect, useCallback } from 'react';
import {
  getRebalanceAdvisor,
  saveRebalanceTargets,
  type RebalanceAdvisorData,
  type RebalanceTarget,
} from '../api/rebalance-advisor';

export interface UseRebalancingResult {
  advisor: RebalanceAdvisorData | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveError: string | null;
  refresh: () => void;
  saveTargets: (
    targets: Array<{ symbol: string; targetPct: number; tolerancePct?: number }>,
  ) => Promise<RebalanceTarget[]>;
}

export function useRebalancing(userId: string | undefined): UseRebalancingResult {
  const [advisor, setAdvisor] = useState<RebalanceAdvisorData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getRebalanceAdvisor(userId);
      setAdvisor(data);
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : 'Failed to load rebalancing data';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveTargets = useCallback(
    async (
      targets: Array<{ symbol: string; targetPct: number; tolerancePct?: number }>,
    ): Promise<RebalanceTarget[]> => {
      if (!userId) throw new Error('No userId');
      setSaving(true);
      setSaveError(null);
      try {
        const result = await saveRebalanceTargets(userId, targets);
        // Reload advisor data to reflect new targets + drift
        await load();
        return result.targets;
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Failed to save targets';
        setSaveError(msg);
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [userId, load],
  );

  return { advisor, loading, error, saving, saveError, refresh: load, saveTargets };
}
